import { useTranslation } from "react-i18next";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { SAPTreeView } from "../SAPTreeView";
import { SAPTreeViewOptimized } from "../SAPTreeViewOptimized";
import { SAPTreeViewVirtualized } from "../SAPTreeViewVirtualized";
import LazyMachineGrid from "../LazyMachineGrid";
import GlobeView from "../GlobeView";
import HomeFilterBar from "./HomeFilterBar";
import {
  type Machine,
  type Line,
  type Factory,
} from "../../services/BackendDataService";
import { useNavigate } from "react-router-dom";
import LineCard from "../LineCard";
import { useMemo, useState } from "react";
import { useThemeClasses } from "../../providers/ThemeProvider";
import { cx, useMediaQuery } from "@digitalisation/enlixy-comp";

interface AllTabProps {
  viewMode: "cards" | "tree" | "globe";
  setViewMode: (mode: "cards" | "tree" | "globe") => void;
  expandedNodes: Set<string>;
  setExpandedNodes: (nodes: Set<string>) => void;
}

const AllTab = ({
  viewMode,
  setViewMode,
  expandedNodes,
  setExpandedNodes,
}: AllTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeClasses = useThemeClasses();

  // Collapsible sections state - persist to localStorage
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => {
      try {
        const saved = localStorage.getItem("pc-collapsed-sections");
        if (saved) {
          return new Set(JSON.parse(saved));
        }
      } catch (e) {
        console.error("Error loading collapsed sections:", e);
      }
      return new Set();
    },
  );

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      // Save to localStorage
      try {
        localStorage.setItem(
          "pc-collapsed-sections",
          JSON.stringify([...newSet]),
        );
      } catch (e) {
        console.error("Error saving collapsed sections:", e);
      }
      return newSet;
    });
  };

  // Below Tailwind's sm breakpoint (640 px)
  const isMobile = useMediaQuery("(max-width: 639.98px)");

  // Get store instance once to avoid multiple subscriptions
  const store = useSAPDataStore();
  const isLoading = useSAPDataStore((state) => state.loading);
  // `dataStatus` (not `lastFetched`) is the "a fetch has settled" signal: it is
  // set on every outcome including failures, so a failed fetch can no longer
  // leave this view on an endless spinner.
  const dataStatus = useSAPDataStore((state) => state.dataStatus);
  const storeError = useSAPDataStore((state) => state.error);
  const forceRefresh = useSAPDataStore((state) => state.forceRefresh);

  // Use memoized filtered data to prevent infinite loops
  const filteredData = useMemo(() => {
    try {
      const result = {
        sapTreeData: store.getFilteredSAPTreeData(),
        machines: store.getFilteredMachines(),
        lines: store.getFilteredLines(),
        searchTags: store.searchTags,
        searchOperators: store.searchOperators,
      };

      return result;
    } catch (error) {
      console.error("Error filtering data:", error);
      return {
        sapTreeData: {},
        machines: [],
        lines: [],
        searchTags: [],
        searchOperators: [],
      };
    }
  }, [
    store.searchTags,
    store.searchOperators,
    store.showOnlyWithData,
    store.machines,
    store.lines,
    store.sapTreeData,
  ]);

  const {
    sapTreeData: filteredSAPTreeData,
    machines: filteredMachines,
    lines: filteredLines,
  } = filteredData;

  // Navigation handlers for machines and lines
  const handleLineClick = (line: Line) => {
    navigate(`/line/${line.linieBK}`, {
      state: {
        lineName: line.linieName,
        factory: line.factoryName || "1201 (Factory Name)",
        machineCount: line.maschinen?.length?.toString() || "0",
        lineType: "Production Line",
        supervisor: "John Smith",
      },
    });
  };

  // Count total nodes in the tree for performance optimization
  const treeNodeCount = useMemo(() => {
    let count = 0;
    Object.values(filteredSAPTreeData).forEach((group) => {
      count += 1; // group
      group.regionen?.forEach((region) => {
        count += 1; // region
        region.laender?.forEach((country) => {
          count += 1; // country
          country.kunden?.forEach((customer) => {
            count += 1; // customer
            customer.werke?.forEach((factory) => {
              count += 1; // factory
              factory.linien?.forEach((line) => {
                count += 1; // line
                count += line.maschinen?.length || 0; // machines
              });
            });
          });
        });
      });
    });
    return count;
  }, [filteredSAPTreeData]);

  const handleMachineClick = (machine: Machine) => {
    const machineId = machine.maschinennummer || machine.maschineBK;
    const machineName = machine.machineName || machine.bautyp;
    const lineId = machine.lineId || "";
    const machineType = machine.bautyp;
    navigate(`/machine/${machineId}`, {
      state: {
        machineName,
        lineId,
        machineType,
        factory: machine.factoryName || "1201 (Factory Name)",
      },
    });
  };

  const handleSAPFactoryClick = (_factory: Factory) => {
    // No action needed for factory clicks
  };

  // Retry affordance for the empty / unavailable / error states.
  const handleRetry = () => {
    /*
      Only forceRefresh — deliberately NOT also dispatching
      `sap-data-manual-refresh`.

      Firing both raced: the event clears SAPDataProvider's `hasTried` latch, its
      effect re-runs and calls fetchSAPData while this refresh is still in
      flight, the store's in-flight guard returns immediately, and the provider
      then samples `dataStatus === "loading"` and schedules no retry — silently
      dropping the automatic 10s backoff for that cycle.

      Going direct also means the button still works when NATS is down, where the
      event is a no-op anyway (its listener requires `natsReady`). The provider's
      own pending re-arm timer is untouched, so the automatic cadence continues.

      forceRefresh(null) is safe — the store action resolves a fresh token itself.
    */
    void forceRefresh(null);
  };

  const retryButton = (
    <button
      onClick={handleRetry}
      className="mt-1 rounded-lg px-4 py-2 text-sm font-semibold text-on-brand bg-brand hover:bg-brand-bright transition-colors"
    >
      {t("machineList.retry")}
    </button>
  );

  // SAPTreeView expanded nodes logic
  const handleTreeToggle = (nodeId: string) => {
    // Ignore special force update nodes
    if (nodeId === "__force_update__") {
      return;
    }

    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const isExpanded = (nodeId: string) => {
    return expandedNodes.has(nodeId);
  };

  // Helper function to get all possible node IDs from the SAP data
  const getAllNodeIds = () => {
    const ids: string[] = [];
    Object.entries(filteredSAPTreeData).forEach(([groupKey, customerGroup]) => {
      ids.push(`group-${groupKey}`);
      customerGroup.regionen?.forEach((region) => {
        ids.push(`region-${groupKey}-${region.region}`);
        region.laender?.forEach((country) => {
          ids.push(`country-${groupKey}-${region.region}-${country.land}`);
          country.kunden?.forEach((customer) => {
            ids.push(
              `customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`,
            );
            customer.werke?.forEach((factory) => {
              ids.push(`factory-${customer.kundennummer}-${factory.werkName}`);
              factory.linien?.forEach((line) => {
                ids.push(`line-${factory.werkName}-${line.linieBK}`);
              });
            });
          });
        });
      });
    });
    return ids;
  };

  const handleExpandAll = () => {
    console.log("🌳 AllTab: Expand All clicked");
    const allIds = getAllNodeIds();
    console.log("🌳 AllTab: Expanding all nodes:", allIds.length);
    setExpandedNodes(new Set(allIds));
  };

  const handleCollapseAll = () => {
    console.log("🌳 AllTab: Collapse All clicked");
    console.log("🌳 AllTab: Clearing all expanded nodes");
    setExpandedNodes(new Set());
  };

  // Smart tree component selection based on data size
  const TreeComponent = useMemo(() => {
    if (treeNodeCount < 500) {
      return SAPTreeView; // Original optimized version
    } else if (treeNodeCount < 2000) {
      return SAPTreeViewOptimized; // Flattened structure with search
    } else {
      return SAPTreeViewVirtualized; // Virtualized for large datasets
    }
  }, [treeNodeCount]);

  // Tree component props based on selected component
  const treeProps = useMemo(() => {
    const baseProps = {
      sapData: filteredSAPTreeData,
      onMachineClick: handleMachineClick,
      onLineClick: handleLineClick,
      onFactoryClick: handleSAPFactoryClick,
      expandedNodes,
      onToggleNode: handleTreeToggle,
      isExpanded,
      onExpandAll: handleExpandAll,
      onCollapseAll: handleCollapseAll,
      isMobile, // Add mobile detection
    };

    // Add specific props for optimized versions
    if (TreeComponent === SAPTreeViewOptimized) {
      return {
        ...baseProps,
        maxVisibleNodes: 1000, // Reasonable limit for performance
      };
    } else if (TreeComponent === SAPTreeViewVirtualized) {
      return {
        ...baseProps,
        // Remove fixed height - let component calculate dynamically
      };
    }

    return baseProps;
  }, [
    filteredSAPTreeData,
    handleMachineClick,
    handleLineClick,
    handleSAPFactoryClick,
    expandedNodes,
    handleTreeToggle,
    isExpanded,
    handleExpandAll,
    handleCollapseAll,
    TreeComponent,
    isMobile,
  ]);

  return (
    <div className="flex flex-col h-full space-y-6">
      {/* View Mode Toggle and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-shrink-0">
        <h3
          className={cx(
            themeClasses.textPrimary,
            "text-lg sm:text-xl font-semibold",
          )}
        >
          {t("allData") || "All Production Data"}
        </h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div
            className={cx(
              themeClasses.textSecondary,
              "text-xs sm:text-sm order-last sm:order-first",
            )}
          >
            {filteredMachines.length} machines, {filteredLines.length} lines
          </div>
          {/* Data Filter Toggle - now managed by the store */}
          <div className="order-first sm:order-none">
            <HomeFilterBar />
          </div>
          {/* View Mode Toggle */}
          <div
            className={cx(
              themeClasses.cardBg,
              themeClasses.cardBorder,
              "flex rounded-lg border overflow-hidden w-full sm:w-auto",
            )}
          >
            <button
              onClick={() => setViewMode("cards")}
              className={cx(
                "flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors",
                viewMode === "cards"
                  ? "bg-brand text-on-brand"
                  : cx(themeClasses.textPrimary, themeClasses.hover),
              )}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode("tree")}
              className={cx(
                "flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors",
                themeClasses.borderPrimary,
                "border-l",
                viewMode === "tree"
                  ? "bg-brand text-on-brand"
                  : cx(themeClasses.textPrimary, themeClasses.hover),
              )}
            >
              Tree
            </button>
            <button
              onClick={() => setViewMode("globe")}
              className={cx(
                "flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors",
                themeClasses.borderPrimary,
                "border-l",
                viewMode === "globe"
                  ? "bg-brand text-on-brand"
                  : cx(themeClasses.textPrimary, themeClasses.hover),
              )}
            >
              Globe
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0">
        {/* Loading state: spinner only while a fetch is genuinely in flight
            (or before the provider fired its first one). Every settled outcome
            below renders a real screen, never a spinner. */}
        {isLoading || dataStatus === "idle" || dataStatus === "loading" ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[320px] gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 dark:border-blue-900 border-t-blue-500 animate-spin" />
            <p className={cx(themeClasses.textSecondary, "text-sm")}>
              {t("machineList.loading")}
            </p>
          </div>
        ) : dataStatus === "empty" ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[320px] gap-3 text-center">
            <div className="text-6xl mb-2">⚙️</div>
            <h3
              className={cx(themeClasses.textPrimary, "text-xl font-semibold")}
            >
              {t("machineList.emptyTitle")}
            </h3>
            <p className={cx(themeClasses.textSecondary, "max-w-md text-sm")}>
              {t("machineList.emptyMessage")}
            </p>
            {retryButton}
          </div>
        ) : dataStatus === "unavailable" || dataStatus === "error" ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[320px] gap-3 text-center">
            <div className="text-6xl mb-2">⚠️</div>
            <h3
              className={cx(themeClasses.textPrimary, "text-xl font-semibold")}
            >
              {dataStatus === "unavailable"
                ? t("machineList.unavailableTitle")
                : t("machineList.errorTitle")}
            </h3>
            <p className={cx(themeClasses.textSecondary, "max-w-md text-sm")}>
              {/*
                `unavailable` always prefers the translated string: the store's
                message for it is a generic hardcoded English sentence, so
                `storeError ||` would have made this dead code and shown English
                to German and French users. For `error` the store carries the
                backend's own (untranslatable) text, which is more useful than a
                generic line — the translation is the fallback there.
              */}
              {dataStatus === "unavailable"
                ? t("machineList.unavailableMessage")
                : storeError || t("machineList.errorFallback")}
            </p>
            {retryButton}
          </div>
        ) : viewMode === "tree" ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0">
              <TreeComponent {...treeProps} />
            </div>
          </div>
        ) : viewMode === "globe" ? (
          <div className="h-full">
            <GlobeView
              sapData={filteredSAPTreeData}
              onMachineClick={handleMachineClick}
            />
          </div>
        ) : (
          // Cards View - unified data display
          <div className="space-y-6 sm:space-y-8 overflow-y-auto h-full px-1 sm:px-0">
            {/* Lines Section */}
            <section className="space-y-3 sm:space-y-4">
              <button
                onClick={() => toggleSection("lines")}
                className={cx(
                  themeClasses.textPrimary,
                  "flex items-center gap-2 text-lg sm:text-xl font-semibold px-2 sm:px-0 hover:opacity-80 transition-opacity w-full text-left",
                )}
              >
                <svg
                  className={cx(
                    "w-5 h-5 transition-transform duration-200",
                    collapsedSections.has("lines") ? "" : "rotate-90",
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                {t("productionLines") || "Production Lines"}
                <span
                  className={cx(
                    themeClasses.textSecondary,
                    "text-sm font-normal",
                  )}
                >
                  ({filteredLines.length})
                </span>
              </button>
              {!collapsedSections.has("lines") && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 px-2 sm:px-0">
                  {filteredLines.map((line, index) => {
                    // Create a unique key that's guaranteed to be defined and unique
                    const lineId =
                      line.linieBK || line.linieName || `unknown-line`;
                    const searchContext =
                      filteredData.searchTags.length > 0
                        ? `-search-${filteredData.searchTags.join("-")}`
                        : "";
                    const uniqueKey = `line-${index}-${lineId}${searchContext}`;

                    return (
                      <LineCard
                        key={uniqueKey}
                        line={line}
                        onClick={handleLineClick}
                      />
                    );
                  })}
                </div>
              )}
            </section>
            {/* Machines Section */}
            <section className="space-y-3 sm:space-y-4">
              <button
                onClick={() => toggleSection("machines")}
                className={cx(
                  themeClasses.textPrimary,
                  "flex items-center gap-2 text-lg sm:text-xl font-semibold px-2 sm:px-0 hover:opacity-80 transition-opacity w-full text-left",
                )}
              >
                <svg
                  className={cx(
                    "w-5 h-5 transition-transform duration-200",
                    collapsedSections.has("machines") ? "" : "rotate-90",
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                {t("allMachines") || "All Machines"}
                <span
                  className={cx(
                    themeClasses.textSecondary,
                    "text-sm font-normal",
                  )}
                >
                  ({filteredMachines.length})
                </span>
              </button>
              {!collapsedSections.has("machines") && (
                <div className="px-2 sm:px-0">
                  <LazyMachineGrid
                    key={`machines-${filteredData.searchTags.join("-")}`}
                    machines={filteredMachines}
                    onMachineClick={handleMachineClick}
                    initialLoadCount={12}
                    loadMoreCount={12}
                  />
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllTab;
